import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Edit2, Plus, X, LayoutDashboard, Users, Mic, Video, Calendar, ShoppingBag, BookOpen, Handshake, Mail, Heart, Gift, UserSquare2, Settings, Image as ImageIcon, Link2, Ticket, Download } from "lucide-react";
import { api, formatZAR, resolveUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import ImageUpload from "../components/site/ImageUpload";
import GalleryPanel from "./GalleryPanel";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "artists", label: "Artists", icon: Mic },
  { key: "directors", label: "Directors", icon: UserSquare2 },
  { key: "videos", label: "Videos", icon: Video },
  { key: "events", label: "Events", icon: Calendar },
  { key: "products", label: "Shop", icon: ShoppingBag },
  { key: "blog", label: "Blog", icon: BookOpen },
  { key: "partners", label: "Partners", icon: Handshake },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "members", label: "Members", icon: Users },
  { key: "donations", label: "Donations", icon: Heart },
  { key: "contacts", label: "Contacts", icon: Mail },
  { key: "newsletter", label: "Newsletter", icon: Gift },
  { key: "settings", label: "Settings", icon: Settings },
];

const SCHEMAS = {
  artists: {
    endpoint: "artists",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "genre", label: "Genre", type: "text", required: true },
      { name: "bio", label: "Bio", type: "textarea", required: true },
      { name: "photo_url", label: "Photo", type: "image", required: true },
      { name: "social_links.instagram", label: "Instagram URL", type: "text" },
      { name: "social_links.youtube", label: "YouTube URL", type: "text" },
      { name: "featured", label: "Featured on home page?", type: "checkbox" },
    ],
  },
  directors: {
    endpoint: "directors",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "role", label: "Role / Title", type: "text", required: true },
      { name: "bio", label: "Bio", type: "textarea", required: true },
      { name: "photo_url", label: "Photo", type: "image", required: true },
      { name: "order", label: "Display Order (1 = first)", type: "number", default: 1 },
    ],
  },
  videos: {
    endpoint: "videos",
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "artist_name", label: "Artist Name", type: "text" },
      { name: "artist_id", label: "Artist ID (optional)", type: "text" },
      { name: "category", label: "Category", type: "select", options: ["Music Videos", "Live Performances", "Behind the Scenes", "Devotionals", "Artist Development"], required: true },
      { name: "youtube_id", label: "YouTube Video ID", type: "text", required: true, help: "e.g. dQw4w9WgXcQ (from YouTube URL after v=)" },
      { name: "thumbnail", label: "Custom Thumbnail (optional — auto-pulled from YouTube if blank)", type: "image" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "is_premium", label: "Members-only?", type: "checkbox" },
      { name: "is_featured", label: "Featured on home?", type: "checkbox" },
    ],
  },
  events: {
    endpoint: "events",
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "event_date", label: "Event Date & Time (24-hour)", type: "datetime-local", required: true, help: "Time is 24-hour (00:00 = midnight, 13:00 = 1 PM, 23:55 = 11:55 PM). All times are stored in SAST (UTC+2)." },
      { name: "venue", label: "Venue", type: "text", required: true },
      { name: "image_url", label: "Event Image", type: "image", required: true },
      { name: "additional_images", label: "Additional Posters (slideshow)", type: "images", help: "Add extra posters to auto-rotate as a slideshow on the event thumbnail. Leave empty to show only the main image." },
      { name: "general_admission", label: "General Admission (open / first-come-first-serve)?", type: "checkbox", default: true, help: "Tick this for free-seating events. Untick to define tiered tickets (e.g. General / Gold / VIP)." },
      { name: "ticket_price", label: "Ticket Price (ZAR) — used only when General Admission is ticked. Set 0 for free events.", type: "number", default: 0 },
      { name: "tiers", label: "Ticket Tiers (used only when General Admission is unticked)", type: "tiers" },
      { name: "show_availability", label: "Show remaining seats to public", type: "checkbox", default: true, help: "When OFF, the public site hides progress bars and \"only X left\" badges. Admin console always shows sold counts." },
      { name: "is_live_stream", label: "Has live stream?", type: "checkbox" },
      { name: "stream_embed_url", label: "Stream Embed URL (YouTube Live / Vimeo embed)", type: "text", help: "e.g. https://www.youtube.com/embed/LIVE_VIDEO_ID" },
      { name: "stream_url", label: "Public Stream URL (fallback)", type: "text" },
    ],
  },
  products: {
    endpoint: "products",
    fields: [
      { name: "name", label: "Product Name", type: "text", required: true },
      { name: "type", label: "Type", type: "select", options: ["digital_music", "merchandise", "book"], required: true },
      { name: "price", label: "Price (ZAR)", type: "number", required: true },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "image_url", label: "Product Image", type: "image", required: true },
      { name: "stock", label: "Stock", type: "number", default: 100 },
      { name: "download_url", label: "Download URL (for digital — can be /api/files/... or external)", type: "text", help: "Leave blank for merchandise/books" },
    ],
  },
  blog: {
    endpoint: "blog",
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "slug", label: "Slug (URL)", type: "text", required: true, help: "e.g. my-post-title" },
      { name: "excerpt", label: "Excerpt", type: "textarea", required: true },
      { name: "content", label: "Content (HTML)", type: "textarea", required: true },
      { name: "cover_image", label: "Cover Image", type: "image", required: true },
      { name: "published", label: "Published?", type: "checkbox", default: true },
    ],
  },
  partners: {
    endpoint: "partners",
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "logo_url", label: "Logo", type: "image", required: true },
      { name: "website_url", label: "Website URL", type: "text", required: true },
      { name: "description", label: "Description", type: "text" },
    ],
  },
};

function getNested(obj, path) {
  return path.split(".").reduce((a, k) => (a ? a[k] : undefined), obj);
}
function setNested(obj, path, val) {
  const keys = path.split(".");
  const last = keys.pop();
  let target = obj;
  for (const k of keys) {
    if (!target[k]) target[k] = {};
    target = target[k];
  }
  target[last] = val;
}

function DateTime24Picker({ value, onChange }) {
  // Value is ISO string. Local date/time inputs use user's local timezone.
  const d = value ? new Date(value) : null;
  const pad = (n) => String(n).padStart(2, "0");
  const datePart = d && !isNaN(d) ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "";
  const hh = d && !isNaN(d) ? pad(d.getHours()) : "";
  const mm = d && !isNaN(d) ? pad(d.getMinutes()) : "";

  const commit = (nextDate, nextHH, nextMM) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    const h = nextHH === "" ? "00" : nextHH;
    const m = nextMM === "" ? "00" : nextMM;
    const [y, mo, dy] = nextDate.split("-").map(Number);
    const local = new Date(y, mo - 1, dy, Number(h), Number(m), 0, 0);
    if (isNaN(local)) return;
    onChange(local.toISOString());
  };

  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const minutes = Array.from({ length: 60 }, (_, i) => pad(i)); // 00..59 (single-minute precision)

  return (
    <div data-testid="datetime-24-picker">
      <div className="grid grid-cols-12 gap-2 items-center">
        <input
          type="date"
          value={datePart}
          onChange={(e) => commit(e.target.value, hh, mm)}
          className="col-span-5 px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm"
          data-testid="event-date-input"
        />
        <select
          value={hh}
          onChange={(e) => commit(datePart, e.target.value, mm)}
          className="col-span-2 px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm bg-white"
          aria-label="Hour (24-hour)"
          data-testid="event-hour-select"
        >
          <option value="">HH</option>
          {hours.map((h) => (<option key={h} value={h}>{h}</option>))}
        </select>
        <span className="col-span-1 text-center text-gray-500 font-mono text-lg leading-none">:</span>
        <select
          value={mm}
          onChange={(e) => commit(datePart, hh, e.target.value)}
          className="col-span-2 px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm bg-white"
          aria-label="Minute"
          data-testid="event-minute-select"
        >
          <option value="">MM</option>
          {minutes.map((m) => (<option key={m} value={m}>{m}</option>))}
        </select>
        <span
          className="col-span-2 text-[10px] tracking-[0.15em] uppercase text-[#C00000] font-semibold text-center"
          title="South African Standard Time — UTC+2. Times are saved in this timezone."
          data-testid="event-timezone-label"
        >
          SAST<br/>(UTC+2)
        </span>
      </div>
    </div>
  );
}

function ImageListEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, url) => onChange(items.map((v, idx) => (idx === i ? url : v)).filter(Boolean));
  const addSlot = () => onChange([...items, ""]);
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="image-list-editor">
      {items.length === 0 && (
        <div className="text-xs text-gray-500 italic">No additional posters yet — the event will show only the main image.</div>
      )}
      {items.map((url, i) => (
        <div key={i} className="flex items-start gap-3 p-3 border border-gray-200 bg-gray-50">
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.15em] uppercase text-gray-500 mb-2">Poster {i + 1}</div>
            <ImageUpload value={url} onChange={(u) => setAt(i, u)} label={`Poster ${i + 1}`} />
          </div>
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="p-2 hover:bg-white text-[#C00000]"
            aria-label={`Remove poster ${i + 1}`}
            data-testid={`remove-poster-${i}`}
          ><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button
        type="button"
        onClick={addSlot}
        className="w-full py-3 border border-dashed border-gray-300 text-xs tracking-[0.15em] uppercase text-gray-600 hover:border-[#1F3864] hover:text-[#1F3864] transition-colors"
        data-testid="add-poster-slot"
      >+ Add Poster</button>
    </div>
  );
}

function TierEditor({ value, onChange }) {
  const tiers = Array.isArray(value) ? value : [];
  const update = (i, patch) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    onChange(next);
  };
  const add = () => onChange([...tiers, { name: "", price: 0, quantity: 0, sold: 0 }]);
  const remove = (i) => onChange(tiers.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="tier-editor">
      {tiers.length === 0 && (
        <div className="text-xs text-gray-500 italic py-2">No tiers yet. Add tiers like General / Gold / VIP if seating is tier-based.</div>
      )}
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 border border-gray-200" data-testid={`tier-row-${i}`}>
          <input value={t.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Tier name" className="col-span-4 px-2 py-1.5 border border-gray-300 text-sm" data-testid={`tier-name-${i}`} />
          <input type="number" value={t.price ?? 0} onChange={(e) => update(i, { price: parseFloat(e.target.value) || 0 })} placeholder="Price" className="col-span-3 px-2 py-1.5 border border-gray-300 text-sm" data-testid={`tier-price-${i}`} />
          <input type="number" value={t.quantity ?? 0} onChange={(e) => update(i, { quantity: parseInt(e.target.value) || 0 })} placeholder="Quantity" className="col-span-3 px-2 py-1.5 border border-gray-300 text-sm" data-testid={`tier-qty-${i}`} />
          <div className="col-span-1 text-xs text-gray-500 text-center">{t.sold || 0}<div className="text-[9px] uppercase">sold</div></div>
          <button type="button" onClick={() => remove(i)} className="col-span-1 p-1.5 hover:text-[#C00000]" title="Remove" data-testid={`tier-remove-${i}`}>
            <X className="w-4 h-4 mx-auto" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs tracking-[0.15em] uppercase text-[#1F3864] hover:text-[#C00000] border border-dashed border-gray-300 hover:border-[#1F3864] px-3 py-2 w-full" data-testid="tier-add">
        <Plus className="w-3 h-3 inline mr-1" /> Add Tier
      </button>
    </div>
  );
}

function EditModal({ schema, item, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const base = {};
    for (const f of schema.fields) {
      const fallback = f.type === "checkbox" ? false : f.type === "tiers" ? [] : f.type === "images" ? [] : "";
      const v = item ? getNested(item, f.name) : (f.default !== undefined ? f.default : fallback);
      setNested(base, f.name, v === undefined ? fallback : v);
    }
    return base;
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    // Coerce numeric fields
    for (const f of schema.fields) {
      if (f.type === "number") {
        const v = getNested(payload, f.name);
        if (v !== undefined && v !== "") setNested(payload, f.name, parseFloat(v));
      }
      if (f.name.startsWith("social_links.") && !payload.social_links) {
        payload.social_links = {};
      }
    }
    // Clean empty social_links
    if (payload.social_links) {
      for (const k of Object.keys(payload.social_links)) {
        if (!payload.social_links[k]) delete payload.social_links[k];
      }
    }
    try {
      const url = item ? `/admin/${schema.endpoint}/${item.id}` : `/admin/${schema.endpoint}`;
      const method = item ? "put" : "post";
      const { data } = await api[method](url, payload);
      toast.success(item ? "Updated" : "Created");
      onSave(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex justify-between items-center">
          <h3 className="font-display text-2xl text-[#1F3864]">{item ? "Edit" : "Create New"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {schema.fields.map((f) => {
            const val = getNested(form, f.name);
            const update = (v) => {
              const next = { ...form };
              setNested(next, f.name, v);
              setForm(next);
            };
            return (
              <div key={f.name}>
                <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">{f.label}{f.required && <span className="text-[#C00000]"> *</span>}</label>
                {f.type === "textarea" ? (
                  <textarea rows={4} required={f.required} value={val || ""} onChange={(e) => update(e.target.value)} className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" />
                ) : f.type === "select" ? (
                  <select required={f.required} value={val || ""} onChange={(e) => update(e.target.value)} className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm bg-white">
                    <option value="">Select...</option>
                    {f.options.map((o) => <option key={o}>{o}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <input type="checkbox" checked={!!val} onChange={(e) => update(e.target.checked)} className="w-5 h-5 accent-[#C00000]" />
                ) : f.type === "image" ? (
                  <ImageUpload value={val} onChange={update} label={f.label} />
                ) : f.type === "images" ? (
                  <ImageListEditor value={val} onChange={update} />
                ) : f.type === "tiers" ? (
                  <TierEditor value={val} onChange={update} />
                ) : f.type === "datetime-local" ? (
                  <DateTime24Picker value={val} onChange={update} />
                ) : (
                  <input type={f.type} required={f.required} value={val ?? ""} onChange={(e) => update(e.target.value)} className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" />
                )}
                {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
              </div>
            );
          })}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-300 text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-red" data-testid="admin-save-button">
              {busy ? "Saving..." : item ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EventPhotosManager({ event, onClose }) {
  const [state, setState] = useState({ photos: [], photos_published: false, photos_auto_publish: false, count: 0 });
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [selectedTiers, setSelectedTiers] = useState([]); // empty = all attendees
  const [sendEmail, setSendEmail] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/photos`);
      setState(data);
    } catch (e) {
      toast.error("Could not load photos");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.id]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/admin/events/${event.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}`);
    e.target.value = "";
    load();
  };

  const removePhoto = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/events/${event.id}/photos/${id}`);
      toast.success("Photo removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleAutoPublish = async () => {
    const next = !state.photos_auto_publish;
    try {
      await api.put(`/admin/events/${event.id}/photos/settings`, { photos_auto_publish: next });
      toast.success(next ? "Auto-publish enabled" : "Auto-publish disabled");
      load();
    } catch { toast.error("Could not save"); }
  };

  const unpublish = async () => {
    if (!window.confirm("Hide gallery from the public?")) return;
    try {
      await api.put(`/admin/events/${event.id}/photos/settings`, { photos_published: false });
      toast.success("Gallery hidden from public");
      load();
    } catch { toast.error("Could not update"); }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/photos/publish`, {
        tier_ids: selectedTiers,
        send_email: sendEmail,
      });
      toast.success(`Published${sendEmail ? ` — ${data.emailed} email${data.emailed === 1 ? "" : "s"} sent` : ""}`);
      setShowPublish(false);
      setSelectedTiers([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const tiers = event.tiers || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="event-photos-manager">
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-4xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#C00000]">Event Photo Gallery</div>
            <h3 className="font-display text-2xl text-[#1F3864] mt-1">{event.title}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100" data-testid="close-photo-mgr"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 p-4">
            <div className="flex items-center gap-6 flex-wrap text-sm">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Photos</div>
                <div className="font-semibold text-[#1F3864] text-lg">{state.count}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Public Gallery</div>
                <div className={`font-semibold ${state.photos_published ? "text-green-700" : "text-gray-500"}`} data-testid="publish-status">
                  {state.photos_published ? "Live" : "Draft"}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" title="Photos become public as soon as they're uploaded">
                <input type="checkbox" checked={state.photos_auto_publish} onChange={toggleAutoPublish} data-testid="toggle-auto-publish" />
                <span className="text-xs tracking-[0.15em] uppercase text-gray-700">Auto-publish new uploads</span>
              </label>
            </div>
            <div className="flex gap-2">
              {state.photos_published && (
                <>
                  <a href={`/events/${event.id}/photos`} target="_blank" rel="noreferrer" className="text-xs tracking-[0.15em] uppercase border border-[#1F3864] text-[#1F3864] px-3 py-2 hover:bg-[#1F3864] hover:text-white transition" data-testid="view-public-gallery">View Public</a>
                  <button onClick={unpublish} className="text-xs tracking-[0.15em] uppercase border border-gray-300 text-gray-700 px-3 py-2 hover:bg-gray-100 transition" data-testid="unpublish-btn">Unpublish</button>
                </>
              )}
              <button onClick={() => setShowPublish(true)} disabled={state.count === 0} className="btn-primary-red disabled:opacity-60" data-testid="open-publish-modal">
                {state.photos_published ? "Republish & Notify" : "Publish & Notify"}
              </button>
            </div>
          </div>

          {/* Uploader */}
          <label className="block border-2 border-dashed border-gray-300 p-8 text-center cursor-pointer hover:border-[#1F3864] transition-colors">
            <input type="file" accept="image/*" multiple onChange={onUpload} disabled={uploading} className="hidden" data-testid="photo-upload-input" />
            <div className="text-sm text-gray-600">
              {uploading ? "Uploading…" : "Click to select photos (JPG, PNG, WebP, HEIC — up to 20 MB each)"}
            </div>
            <div className="text-xs text-gray-400 mt-1">You can select many photos at once.</div>
          </label>

          {/* Grid */}
          {state.photos.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No photos yet.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="admin-photo-grid">
              {state.photos.map((p) => (
                <div key={p.id} className="relative group aspect-square bg-gray-100">
                  <img src={resolveUrl(p.url)} alt={p.original_filename} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-1 right-1 w-6 h-6 bg-[#C00000] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-photo-${p.id}`}
                    aria-label="Remove"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Publish modal */}
      {showPublish && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setShowPublish(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-md w-full p-6" data-testid="publish-modal">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-1">Publish & Notify</div>
            <h4 className="font-display text-xl text-[#1F3864] mb-4">Send photo link to attendees?</h4>

            <label className="flex items-center gap-2 mb-4 text-sm">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="publish-send-email" />
              <span>Email a download link to attendees</span>
            </label>

            {sendEmail && tiers.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-gray-600 mb-2">Which ticket tiers to notify?</div>
                <label className="flex items-center gap-2 text-sm py-1">
                  <input
                    type="checkbox"
                    checked={selectedTiers.length === 0}
                    onChange={() => setSelectedTiers([])}
                    data-testid="tier-all"
                  />
                  <span className="font-semibold">All attendees</span>
                </label>
                {tiers.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm py-1 pl-4">
                    <input
                      type="checkbox"
                      checked={selectedTiers.includes(t.id)}
                      onChange={(e) => {
                        setSelectedTiers((prev) => e.target.checked ? [...prev, t.id] : prev.filter((x) => x !== t.id));
                      }}
                      data-testid={`tier-check-${t.id}`}
                    />
                    <span>Only <strong>{t.name}</strong> holders</span>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
              <button onClick={() => setShowPublish(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-testid="publish-cancel">Cancel</button>
              <button onClick={doPublish} disabled={publishing} className="btn-primary-red disabled:opacity-60" data-testid="publish-confirm">
                {publishing ? "Publishing…" : "Publish Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketRosterModal({ event, onClose }) {
  const [tickets, setTickets] = useState(null);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/admin/events/${event.id}/tickets`)
      .then(({ data }) => setTickets(data))
      .catch(() => { setTickets([]); toast.error("Could not load tickets"); });
  }, [event.id]);

  const filtered = (tickets || []).filter((t) => {
    if (!q) return true;
    const hay = `${t.ticket_code} ${t.buyer_name} ${t.buyer_email} ${t.buyer_phone} ${t.tier_name}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const total = tickets?.length || 0;
  const revenue = (tickets || []).reduce((s, t) => s + (t.price_paid || 0), 0);
  const usedCount = (tickets || []).filter((t) => t.status === "used").length;

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/events/${event.id}/tickets.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = event.title.replace(/[^a-zA-Z0-9\-_]/g, "_").slice(0, 40) || "event";
      a.download = `${safe}_tickets.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel download started");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Excel download failed:", err?.message || err);
      toast.error("Could not download Excel");
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="ticket-roster-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-6xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#C00000]">Ticket Roster</div>
            <h3 className="font-display text-2xl text-[#1F3864] mt-1">{event.title}</h3>
            <div className="text-xs text-gray-500 mt-1">
              {new Date(event.event_date).toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {event.venue}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadXlsx}
              disabled={downloading || total === 0}
              className="btn-primary-red disabled:opacity-60"
              data-testid="download-tickets-xlsx"
            ><Download className="w-4 h-4" /> {downloading ? "Downloading…" : "Download Excel"}</button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100" data-testid="close-ticket-roster"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3">
            <div className="border-l-4 border-[#1F3864] pl-3 py-2">
              <div className="text-2xl font-semibold text-[#1F3864]">{total}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Tickets Sold</div>
            </div>
            <div className="border-l-4 border-green-600 pl-3 py-2">
              <div className="text-2xl font-semibold text-green-700">{formatZAR(revenue)}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Revenue</div>
            </div>
            <div className="border-l-4 border-gray-400 pl-3 py-2">
              <div className="text-2xl font-semibold text-gray-700">{usedCount}<span className="text-sm text-gray-400"> / {total}</span></div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500">Checked In</div>
            </div>
          </div>

          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by ticket #, name, email, phone or tier…"
            className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm"
            data-testid="ticket-search"
          />

          {tickets === null ? (
            <div className="text-center py-8 text-gray-500">Loading tickets…</div>
          ) : total === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Ticket className="w-8 h-8 mx-auto mb-3 text-gray-300" />
              No tickets sold yet.
            </div>
          ) : (
            <div className="border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Ticket #</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Tier</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">First Name</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Surname</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Email</th>
                    <th className="text-left p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600 hidden md:table-cell">Phone</th>
                    <th className="text-right p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Price</th>
                    <th className="text-center p-3 text-[10px] tracking-[0.15em] uppercase text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id} className={`border-t border-gray-100 ${t.status === "used" ? "bg-gray-50 text-gray-400" : ""}`} data-testid={`ticket-row-${t.ticket_code}`}>
                      <td className="p-3 font-mono font-semibold text-[#1F3864]">{t.ticket_code}</td>
                      <td className="p-3">{t.tier_name}</td>
                      <td className="p-3">{t.buyer_first_name}</td>
                      <td className="p-3">{t.buyer_surname}</td>
                      <td className="p-3 text-gray-600">{t.buyer_email}</td>
                      <td className="p-3 text-gray-600 hidden md:table-cell">{t.buyer_phone || "—"}</td>
                      <td className="p-3 text-right">{formatZAR(t.price_paid || 0)}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase ${t.status === "valid" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-gray-500">No tickets match "{q}"</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CrudTable({ section }) {
  const schema = SCHEMAS[section];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [photoMgr, setPhotoMgr] = useState(null);
  const [ticketRoster, setTicketRoster] = useState(null);

  const load = async () => {
    let path;
    if (section === "blog") path = "/blog";
    else if (section === "directors") path = "/directors";
    else path = `/${section}`;
    const { data } = await api.get(path);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section]);

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${schema.endpoint}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`admin-${section}-crud`}>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-display text-3xl text-[#1F3864] capitalize">{section}</h2>
        <button onClick={() => setCreating(true)} className="btn-primary-red" data-testid={`admin-create-${section}`}>
          <Plus className="w-4 h-4" /> New
        </button>
      </div>
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>
            <th className="text-left p-3">Name / Title</th>
            <th className="text-left p-3 hidden md:table-cell">Details</th>
            <th className="text-right p-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100" data-testid={`admin-row-${it.id}`}>
                <td className="p-3 font-medium">{it.name || it.title || it.slug}</td>
                <td className="p-3 text-gray-500 hidden md:table-cell">
                  {section === "products" && formatZAR(it.price)}
                  {section === "events" && new Date(it.event_date).toLocaleDateString()}
                  {section === "artists" && it.genre}
                  {section === "videos" && it.category}
                  {section === "partners" && it.website_url}
                </td>
                <td className="p-3 text-right">
                  {section === "events" && (
                    <>
                      <button
                        onClick={() => setTicketRoster(it)}
                        className="p-2 hover:bg-gray-100"
                        title="View ticket roster"
                        data-testid={`admin-tickets-${it.id}`}
                      ><Ticket className="w-4 h-4 text-[#1F3864]" /></button>
                      <button
                        onClick={async () => {
                          const url = `${window.location.origin}/events/${it.id}`;
                          try {
                            await navigator.clipboard.writeText(url);
                            toast.success("Share link copied — paste it anywhere");
                          } catch {
                            toast.info(url);
                          }
                        }}
                        className="p-2 hover:bg-gray-100"
                        title="Copy shareable link"
                        data-testid={`admin-copy-link-${it.id}`}
                      ><Link2 className="w-4 h-4 text-[#1F3864]" /></button>
                      <button
                        onClick={() => setPhotoMgr(it)}
                        className="p-2 hover:bg-gray-100"
                        title="Manage photos"
                        data-testid={`admin-photos-${it.id}`}
                      ><ImageIcon className="w-4 h-4 text-[#1F3864]" /></button>
                    </>
                  )}
                  <button onClick={() => setEditing(it)} className="p-2 hover:bg-gray-100" data-testid={`admin-edit-${it.id}`}><Edit2 className="w-4 h-4 text-[#1F3864]" /></button>
                  <button onClick={() => del(it.id)} className="p-2 hover:bg-gray-100" data-testid={`admin-delete-${it.id}`}><Trash2 className="w-4 h-4 text-[#C00000]" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="p-10 text-center text-gray-500">No items yet. Click New to create.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {(editing || creating) && (
        <EditModal schema={schema} item={editing} onClose={() => { setEditing(null); setCreating(false); }} onSave={load} />
      )}
      {photoMgr && (
        <EventPhotosManager event={photoMgr} onClose={() => { setPhotoMgr(null); load(); }} />
      )}
      {ticketRoster && (
        <TicketRosterModal event={ticketRoster} onClose={() => setTicketRoster(null)} />
      )}
    </div>
  );
}

function ReadOnlyTable({ endpoint, columns, testId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <div className="bg-white border border-gray-200 overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-gray-50"><tr>{columns.map((c) => <th key={c.key} className="text-left p-3">{c.label}</th>)}</tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id || i} className="border-t border-gray-100">
              {columns.map((c) => <td key={c.key} className="p-3">{c.render ? c.render(it[c.key], it) : (it[c.key] ?? "—")}</td>)}
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={columns.length} className="p-10 text-center text-gray-500">No records.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-3xl text-[#1F3864] mb-8">Overview</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: "Members", v: stats.members },
          { label: "Artists", v: stats.artists },
          { label: "Videos", v: stats.videos },
          { label: "Events", v: stats.events },
          { label: "Products", v: stats.products },
          { label: "Newsletter Subs", v: stats.newsletter_subscribers },
          { label: "Contact Msgs", v: stats.contact_submissions },
          { label: "Total Revenue", v: formatZAR(stats.total_revenue || 0) },
        ].map((s, i) => (
          <div key={i} className="bg-white p-6 border-l-4 border-[#C00000]">
            <div className="text-3xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>{s.v ?? "—"}</div>
            <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      {stats.revenue_by_type && Object.keys(stats.revenue_by_type).length > 0 && (
        <div className="mt-10 bg-white p-6 border border-gray-200">
          <h3 className="font-display text-xl text-[#1F3864] mb-4">Revenue by Type</h3>
          <ul className="space-y-2 text-sm">
            {Object.entries(stats.revenue_by_type).map(([k, v]) => (
              <li key={k} className="flex justify-between border-b border-gray-100 pb-2 capitalize">
                <span>{k}</span><span className="font-semibold">{formatZAR(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <RecoveryFunnel />
      <PayFastReconciler />
    </div>
  );
}

function PayFastReconciler() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/payfast/pending")
      .then((r) => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const reconcile = async (mid) => {
    if (!window.confirm(`Force-complete transaction ${mid}? This will issue the ticket/order and send the confirmation email. Only do this after verifying the payment in your PayFast dashboard.`)) return;
    setBusy(mid);
    try {
      await api.post(`/admin/payfast/reconcile/${mid}`);
      toast.success("Reconciled — order fulfilled and email sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-10 bg-white p-6 border border-gray-200" data-testid="payfast-reconciler">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1F3864]">Stuck PayFast Payments</h3>
          <div className="text-xs tracking-[0.15em] uppercase text-gray-500 mt-1">
            Payments confirmed by PayFast but never fulfilled. Verify in your PayFast dashboard, then reconcile.
          </div>
        </div>
        <button
          onClick={load}
          className="text-xs tracking-[0.15em] uppercase border border-[#1F3864] text-[#1F3864] px-3 py-2 hover:bg-[#1F3864] hover:text-white transition"
          data-testid="reload-pending-payments"
        >
          Refresh
        </button>
      </div>
      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200">
          No stuck payments. Everything is fulfilled.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.2em] uppercase text-gray-500 border-b border-gray-200">
                <th className="py-2 px-2 text-left">Reference</th>
                <th className="py-2 px-2 text-left">Type</th>
                <th className="py-2 px-2 text-left">Amount</th>
                <th className="py-2 px-2 text-left">Buyer</th>
                <th className="py-2 px-2 text-left">Started</th>
                <th className="py-2 px-2 text-left">Status</th>
                <th className="py-2 px-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const buyerName = r.metadata?.donor_name || r.metadata?.customer_name || r.metadata?.buyer_name || "";
                const buyerEmail = r.metadata?.donor_email || r.metadata?.customer_email || r.metadata?.buyer_email || "";
                return (
                  <tr key={r.session_id} className="border-b border-gray-100" data-testid={`pending-row-${r.session_id}`}>
                    <td className="py-2 px-2 font-mono text-xs text-[#1F3864]">{r.session_id}</td>
                    <td className="py-2 px-2 capitalize">{r.type}</td>
                    <td className="py-2 px-2">{formatZAR(r.amount || 0)}</td>
                    <td className="py-2 px-2">
                      <div className="text-xs">{buyerName}</div>
                      <div className="text-[10px] text-gray-500">{buyerEmail}</div>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-600">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 px-2 text-xs">{r.payment_status || r.status || "pending"}</td>
                    <td className="py-2 px-2 text-right">
                      <button
                        onClick={() => reconcile(r.session_id)}
                        disabled={busy === r.session_id}
                        className="text-[10px] tracking-[0.2em] uppercase bg-[#C00000] text-white px-3 py-1.5 hover:bg-[#a00000] transition disabled:opacity-60"
                        data-testid={`reconcile-${r.session_id}`}
                      >
                        {busy === r.session_id ? "Working…" : "Reconcile"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecoveryFunnel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/reminders/analytics").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/reminders/run");
      toast.success(`Scan complete — ${r.sent} reminder${r.sent === 1 ? "" : "s"} sent`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const { counts = {}, revenue = {}, totals = {}, recovered_by_type = {}, pending_by_type = {} } = data;
  const stages = [
    { key: "pending",   label: "Pending",    hint: "< 1h old, no reminder yet", color: "#1F3864" },
    { key: "reminded",  label: "Reminded",   hint: "Reminder sent, awaiting payment", color: "#C00000" },
    { key: "recovered", label: "Recovered",  hint: "Paid after reminder", color: "#16a34a" },
    { key: "abandoned", label: "Abandoned",  hint: "> 24h old, window closed", color: "#6b7280" },
    { key: "completed", label: "Completed",  hint: "Paid without a nudge", color: "#0f766e" },
  ];

  return (
    <div className="mt-10 bg-white p-6 border border-gray-200" data-testid="recovery-funnel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1F3864]">Checkout Recovery Funnel</h3>
          <div className="text-xs tracking-[0.15em] uppercase text-gray-500 mt-1">
            This month · {totals.started || 0} checkouts started · {totals.conversion_rate_pct || 0}% converted
          </div>
        </div>
        <button
          onClick={runScan}
          disabled={busy}
          className="text-xs tracking-[0.15em] uppercase border border-[#1F3864] text-[#1F3864] px-3 py-2 hover:bg-[#1F3864] hover:text-white transition disabled:opacity-60"
          data-testid="run-reminder-scan"
        >
          {busy ? "Scanning…" : "Run Scan Now"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {stages.map((s) => (
          <div key={s.key} className="border-l-4 pl-3 py-2" style={{ borderColor: s.color }} data-testid={`funnel-${s.key}`}>
            <div className="text-2xl font-semibold text-[#1F3864]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {counts[s.key] ?? 0}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mt-0.5">{s.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">{formatZAR(revenue[s.key] || 0)}</div>
            <div className="text-[10px] text-gray-400 mt-1">{s.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-6">
        <div className="p-4 bg-[#F5F5F5]">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2">Revenue Rescued</div>
          <div className="text-3xl font-semibold text-[#16a34a]" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="revenue-rescued">
            {formatZAR(revenue.recovered || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {totals.recovery_rate_pct || 0}% of at-risk checkouts recovered this month
          </div>
          {Object.keys(recovered_by_type).length > 0 && (
            <ul className="mt-3 text-xs space-y-1">
              {Object.entries(recovered_by_type).map(([k, v]) => (
                <li key={k} className="flex justify-between text-gray-600 capitalize">
                  <span>{k}</span><span className="font-medium">{formatZAR(v)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 bg-[#F5F5F5]">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2">At Risk Right Now</div>
          <div className="text-3xl font-semibold text-[#C00000]" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="revenue-at-risk">
            {formatZAR((revenue.pending || 0) + (revenue.reminded || 0))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {(counts.pending || 0) + (counts.reminded || 0)} checkouts pending. Reminders auto-send every 15 min.
          </div>
          {Object.keys(pending_by_type).length > 0 && (
            <ul className="mt-3 text-xs space-y-1">
              {Object.entries(pending_by_type).map(([k, v]) => (
                <li key={k} className="flex justify-between text-gray-600 capitalize">
                  <span>{k}</span><span className="font-medium">{formatZAR(v)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [form, setForm] = useState({ ga4_id: "", adsense_id: "", fb_pixel_id: "", google_site_verification: "", whatsapp_number: "", organisation_email: "", organisation_phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm({ ...form, ...r.data })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-display text-3xl text-[#1F3864] mb-6">Site Settings</h2>
      <form onSubmit={save} className="bg-white border border-gray-200 p-8 max-w-2xl space-y-5">
        <div className="pb-4 border-b border-gray-100">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-4">Analytics & Marketing</div>
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Google Analytics 4 Measurement ID</label>
          <input value={form.ga4_id || ""} onChange={(e) => setForm({ ...form, ga4_id: e.target.value })} placeholder="G-XXXXXXXXXX" className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-ga4-id" />
          <div className="text-xs text-gray-500 mt-1">Get this from analytics.google.com → Admin → Data Streams</div>
        </div>
        <div className="pb-4 border-b border-gray-100">
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Google AdSense Publisher ID</label>
          <input value={form.adsense_id || ""} onChange={(e) => setForm({ ...form, adsense_id: e.target.value })} placeholder="ca-pub-XXXXXXXXXXXXXXXX" className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-adsense-id" />
          <div className="text-xs text-gray-500 mt-1">Ad script will be injected site-wide once verified.</div>
        </div>
        <div className="pb-4 border-b border-gray-100">
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Facebook Pixel ID</label>
          <input value={form.fb_pixel_id || ""} onChange={(e) => setForm({ ...form, fb_pixel_id: e.target.value })} placeholder="123456789012345" className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-fb-pixel-id" />
        </div>
        <div className="pb-4 border-b border-gray-100">
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Google Search Console — Verification Code</label>
          <input value={form.google_site_verification || ""} onChange={(e) => setForm({ ...form, google_site_verification: e.target.value })} placeholder="paste the content=&quot;...&quot; value only" className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm font-mono" data-testid="input-gsv" />
          <div className="text-xs text-gray-500 mt-1">
            In Search Console pick <em>HTML tag</em> as the verification method. Paste only the value inside <code className="text-[#C00000]">content="..."</code> (not the whole tag). Save, redeploy, then click "Verify" in Search Console. After verification, submit <code className="text-[#C00000]">https://nextgenpraise.com/api/sitemap.xml</code> in <em>Sitemaps</em>.
          </div>
        </div>
        <div className="pt-2">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-4">Contact Info</div>
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">WhatsApp Number (digits only, with country code)</label>
          <input value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="27735465752" className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm mb-4" />
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Organisation Email</label>
          <input value={form.organisation_email || ""} onChange={(e) => setForm({ ...form, organisation_email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm mb-4" />
          <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">Organisation Phone</label>
          <input value={form.organisation_phone || ""} onChange={(e) => setForm({ ...form, organisation_phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" />
        </div>
        <div className="pt-4 border-t border-gray-100">
          <button type="submit" disabled={busy} className="btn-primary-red" data-testid="admin-settings-save">
            {busy ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [section, setSection] = useState("overview");

  if (loading) return <div className="py-32 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-[80vh] bg-[#F5F5F5]">
      <section className="bg-[#0B1526] text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3">Admin Console</div>
          <h1 className="font-display text-4xl sm:text-5xl">Next Gen Praise — Control Room</h1>
        </div>
      </section>

      <section className="py-6 border-b border-gray-200 bg-white sticky top-20 z-20 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 min-w-max">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`flex items-center gap-2 px-4 py-2 text-xs tracking-[0.15em] uppercase border whitespace-nowrap ${
                section === s.key ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700 hover:border-[#1F3864]"
              }`}
              data-testid={`admin-nav-${s.key}`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          ))}
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {section === "overview" && <Overview />}
          {["artists", "directors", "videos", "events", "products", "blog", "partners"].includes(section) && <CrudTable section={section} />}
          {section === "gallery" && <GalleryPanel />}
          {section === "settings" && <SettingsPanel />}
          {section === "members" && (
            <>
              <h2 className="font-display text-3xl text-[#1F3864] mb-6">Members</h2>
              <ReadOnlyTable endpoint="/admin/members" testId="admin-members-table" columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "membership_tier", label: "Tier" },
                { key: "auth_provider", label: "Provider" },
                { key: "is_admin", label: "Admin", render: (v) => v ? "✓" : "" },
                { key: "created_at", label: "Joined", render: (v) => new Date(v).toLocaleDateString() },
              ]} />
            </>
          )}
          {section === "donations" && (
            <>
              <h2 className="font-display text-3xl text-[#1F3864] mb-6">Donations</h2>
              <ReadOnlyTable endpoint="/admin/donations" testId="admin-donations-table" columns={[
                { key: "donor_name", label: "Donor" },
                { key: "donor_email", label: "Email" },
                { key: "amount", label: "Amount", render: (v) => formatZAR(v) },
                { key: "recurring", label: "Recurring", render: (v) => v ? "Monthly" : "Once-off" },
                { key: "created_at", label: "Date", render: (v) => new Date(v).toLocaleDateString() },
              ]} />
            </>
          )}
          {section === "contacts" && (
            <>
              <h2 className="font-display text-3xl text-[#1F3864] mb-6">Contact Submissions</h2>
              <ReadOnlyTable endpoint="/admin/contacts" testId="admin-contacts-table" columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "subject", label: "Subject" },
                { key: "message", label: "Message", render: (v) => <div className="max-w-md truncate">{v}</div> },
                { key: "created_at", label: "Date", render: (v) => new Date(v).toLocaleDateString() },
              ]} />
            </>
          )}
          {section === "newsletter" && (
            <>
              <h2 className="font-display text-3xl text-[#1F3864] mb-6">Newsletter Subscribers</h2>
              <ReadOnlyTable endpoint="/admin/newsletter" testId="admin-newsletter-table" columns={[
                { key: "email", label: "Email" },
                { key: "subscribed_at", label: "Subscribed", render: (v) => new Date(v).toLocaleDateString() },
              ]} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
